import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ParameterContext } from '@contexts/index';

import { useSession } from '@hooks/useSession';

import type { ParameterValue } from '@models/reference-range';

import {
  checkParamGapOrOverlap,
  determineInclusivity,
} from '@utils/reference_range';

import {
  useDeleteCustomParametersMutation, // useSetCustomParameterMutation,
  useSetCustomParametersMutation,
} from 'generated/reference_range.generated';
import { ReferenceRangeRiskLevel } from 'generated/types';

import { useResponseHandler } from './useResponseHandler';

const messages = defineMessages({
  gap: {
    id: 'insights.parameter.validation.gap',
    defaultMessage: 'Gap: There is a gap between {value1} and {value2}',
  },
  overlap: {
    id: 'insights.parameter.validation.overlap',
    defaultMessage: 'Overlap: {value1} cannot be greater than {value2}',
  },
});

export function useParameter() {
  const context = React.useContext(ParameterContext);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (context === undefined) {
    throw new Error(`useParameter must be used within a ParameterProvider`);
  }

  const { state, category, code, referenceRange, refetchReferenceRange } =
    context;
  const { formatMessage } = useIntl();
  const {
    state: { activeProfile },
  } = useSession();
  const vitall_patient_id = activeProfile?.vitall_patient_id ?? '';
  const { handleSuccess, handleError } = useResponseHandler({
    handlerType: 'snackbar',
  });

  // const [upsertCustomRange] = useSetCustomParameterMutation();
  const [setCustomParameters] = useSetCustomParametersMutation({
    onError: (error) => handleError(error),
    onCompleted: () => handleSuccess(),
  });
  const [deleteCustomRefRanges] = useDeleteCustomParametersMutation({
    onError: (error) => handleError(error),
    onCompleted: () => handleSuccess(),
  });

  const formRefMap = new Map([
    [ReferenceRangeRiskLevel.low, state.lowRef],
    [ReferenceRangeRiskLevel.normal, state.normalRef],
    [ReferenceRangeRiskLevel.mild, state.mildRef],
    [ReferenceRangeRiskLevel.moderate, state.moderateRef],
    [ReferenceRangeRiskLevel.high, state.highRef],
    [ReferenceRangeRiskLevel.crisis, state.crisisRef],
    [ReferenceRangeRiskLevel.below_target, state.belowTargetRef],
    [ReferenceRangeRiskLevel.target, state.targetRef],
    [ReferenceRangeRiskLevel.ideal, state.idealRef],
  ]);

  function getNextItem(currentKey: ReferenceRangeRiskLevel) {
    const iterator = formRefMap.entries();
    let current = iterator.next();
    while (!current.done) {
      const [key] = current.value;
      if (key === currentKey) {
        const next = iterator.next();
        if (next.done) {
          return undefined;
        }
        const [nextKey, nextValue] = next.value;
        if (!nextValue?.current) {
          return getNextItem(nextKey);
        }
        return next.value;
      }
      current = iterator.next();
    }
    return undefined;
  }

  const handleInputValidation = async () => {
    const result: ParameterValue[] = [];
    let hasError: boolean = false;
    // Loop over form refs

    for (const riskLevel of formRefMap.keys()) {
      const ref = formRefMap.get(riskLevel);
      if (ref?.current) {
        await ref.current.submitForm();
        if (ref.current.errors.parameters || ref.current.errors.submit) {
          hasError = true;
        }
        for (const [index, param] of ref.current.values.parameters.entries()) {
          const { expression, lower_bound, parameter_id, upper_bound } = param;
          const initial = ref.current.initialValues.parameters.find(
            (a) => a.parameter_id === parameter_id
          );
          const nextItem = getNextItem(riskLevel);
          const nextParamItem = nextItem?.[1];

          // Check for overlapping/gaping values
          const validationIssue = checkParamGapOrOverlap(
            param,
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            nextParamItem?.current?.values.parameters.at(index)
          );
          if (validationIssue) {
            const { currentValue, nextValue, type } = validationIssue;
            ref.current.setErrors({
              submit: formatMessage(
                type === 'overlap' ? messages.overlap : messages.gap,
                {
                  value1: currentValue,
                  value2: nextValue,
                }
              ),
            });
            hasError = true;
          }

          // Only send updated values
          if (
            !result.find((a) => a.parameter_id === parameter_id) &&
            (initial?.expression !== expression ||
              initial.lower_bound !== lower_bound ||
              initial.upper_bound !== upper_bound)
          ) {
            result.push(param);
          }
        }
      }
    }

    return hasError ? [] : result;
  };

  const handleFormSubmit = async () => {
    // 1. Call handleSubmit in each risk's parameter form for validation
    const result = await handleInputValidation();
    if (!result.length) {
      return;
    }

    // 2. Loop through each parameter to add/update
    const parameters = result.map((input) => {
      const { lower_bound, upper_bound } = input;
      const bounds = determineInclusivity(input);
      return {
        parameter_id: input.parameter_id,
        ...(lower_bound && {
          lower_bound: {
            inclusive: bounds.lower_inclusive,
            value: lower_bound,
          },
        }),
        ...(upper_bound && {
          upper_bound: {
            inclusive: bounds.upper_inclusive,
            value: upper_bound,
          },
        }),
      };
    });
    const { data, errors } = await setCustomParameters({
      variables: {
        input: {
          vitall_patient_id,
          code,
          parameters,
          category,
        },
      },
    });
    if (data?.setCustomParameters && !errors) {
      handleSuccess('setCustomParameters');
    } else {
      handleError(errors, 'setCustomParameters');
    }

    /*
    const updates = await Promise.all(
      result.map((input) => {
        const { lower_bound, upper_bound } = input;
        const bounds = determineInclusivity(input);
        return upsertCustomRange({
          variables: {
            input: {
              vitall_patient_id,
              parameter_id: input.parameter_id,
              ...(lower_bound && {
                lower_bound: {
                  inclusive: bounds.lower_inclusive,
                  value: lower_bound,
                },
              }),
              ...(upper_bound && {
                upper_bound: {
                  inclusive: bounds.upper_inclusive,
                  value: upper_bound,
                },
              }),
            },
          },
        });
      })
    );
    const hasError = updates.some(({ errors }) => Boolean(errors));
    if (!hasError) {
      await refetchReferenceRange();
      handleSuccess('setCustomParameter');
    } else {
      handleError(updates.at(0)?.errors, 'setCustomParameter');
    }
    */
  };

  const handleResetToDefault = async (parameters: ParameterValue[]) => {
    const input = parameters.reduce<string[]>((acc, curr) => {
      if (curr.vitall_patient_id) {
        acc.push(curr.parameter_id);
      }
      return acc;
    }, []);

    await deleteCustomRefRanges({
      variables: {
        input: {
          vitall_patient_id,
          parameter_ids: input,
          code,
        },
      },
    });
    await refetchReferenceRange();
  };

  const handleResetAllParameters = async () => {
    await deleteCustomRefRanges({
      variables: {
        input: {
          vitall_patient_id,
          // Deletes all custom parameters if no ids provided
          parameter_ids: [],
          code,
        },
      },
    });
    await refetchReferenceRange();
  };

  const isStandard = Array.from(referenceRange.entries()).every(([, v]) => {
    const allDefault = v.parameters.every(
      ({ vitall_patient_id }) => !vitall_patient_id
    );
    return allDefault;
  });

  return {
    ...state,
    category,
    code,
    formRefMap,
    handleFormSubmit,
    handleResetAllParameters,
    handleResetToDefault,
    isStandard,
    loading: !state.isInitialized,
    referenceRange,
  };
}
